import { useCallback, useMemo, useState } from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { InfiniteScrollSentinel } from '@/components/data-table/infinite-scroll-sentinel';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { useFxSwaps } from '@/hooks/queries/risk/use-fx-swaps';
import { applyFxSwapClientFilters, type FxRecord, type FxSwapFilters, splitFxSwapFilters } from '@/services/api/fx-swaps';
import { FX_SWAP_TABLE_COLUMNS } from './fx-swap-columns';
import { FxSwapDetailDialog } from './fx-swap-detail-dialog';
import { type FxSwapFilterState, FxSwapFiltersRow } from './fx-swap-filters';

// Row hover: primary @ 10% opacity + pointer cursor (DataTable adds the cursor)
const rowHoverClass = () => 'hover:bg-primary/10';

const getFxSwapRowId = (row: FxRecord): string => `${row.p}#${row.s}`;

export const FxSwapsPanel = () => {
  const [filterState, setFilterState] = useState<FxSwapFilterState>({});
  const [selectedRecord, setSelectedRecord] = useState<FxRecord | null>(null);

  // Flat per-column filter map — the shape the legacy table's filter callback emitted —
  // fed through the hybrid API/client split
  const tableFilters = useMemo<FxSwapFilters>(
    () => ({
      ...(filterState.status && filterState.status.length > 0 ? { status: filterState.status } : {}),
      ...(filterState.createdAt ? { created_at: filterState.createdAt } : {}),
    }),
    [filterState],
  );

  const { apiFilters, clientFilters } = useMemo(() => splitFxSwapFilters(tableFilters), [tableFilters]);

  const query = useFxSwaps(apiFilters);
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isError: isQueryError } = query;

  // Client-side filters run over the accumulated rows without refetching;
  // dormant today — no current column routes here. Identity is preserved when
  // no client filter is active.
  const rows = query.data;
  const visibleRows = useMemo(() => (rows ? applyFxSwapClientFilters(rows, clientFilters) : rows), [rows, clientFilters]);

  // Guarded like trading-panel — TanStack dedupes in-flight fetches anyway, but the
  // guard keeps the three infinite pages consistent (review follow-up)
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isQueryError) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isQueryError]);

  return (
    <div className="flex flex-col gap-4">
      <FxSwapFiltersRow filters={filterState} onChange={setFilterState} />

      {query.isError ? (
        // Non-latching error surface with Retry (legacy latched a fatal alert)
        <QueryErrorBanner message="Please try again later." onRetry={() => query.refetch()} title="Error loading FX swaps" />
      ) : null}

      <DataTable
        columns={FX_SWAP_TABLE_COLUMNS}
        data={visibleRows}
        emptyMessage="No results"
        getRowId={getFxSwapRowId}
        isLoading={query.isPending}
        onRowClick={setSelectedRecord}
        rowClassName={rowHoverClass}
      />

      {/* hasNextPage alone gates the next fetch — no manual refetch-on-scroll path around
          it, so an exhausted cursor chain can never restart from page 1 */}
      {query.isPending || query.isError ? null : query.hasNextPage ? (
        <InfiniteScrollSentinel hasMore isLoading={query.isFetchingNextPage} onLoadMore={handleLoadMore} />
      ) : query.isFetching ? null : (
        // End caption shown only when not loading
        <p className="py-4 text-center text-xs text-muted-foreground">No more FX swaps</p>
      )}

      {selectedRecord ? <FxSwapDetailDialog onClose={() => setSelectedRecord(null)} record={selectedRecord} /> : null}
    </div>
  );
};
FxSwapsPanel.displayName = 'FxSwapsPanel';
